import { useMemo, useState } from "react";
import { buildPreview, PREVIEW_SANDBOX } from "@buildlab/preview";
import { buildProjectMap, type FileView } from "@buildlab/codeview";
import type { FileMap } from "@buildlab/shared";
import type { CardLabel } from "./CodeCards";
import { Arrow } from "./icons";
import { DreamPanel } from "./DreamPanel";
import { TestLab } from "./TestLab";

// The project map (Map artboard, decision 26): the student's visual PRD. The plan comes from
// PROJECT.md; rules, data and art come from the same parse as the event cards.

export function ProjectMap({
  projectId,
  onFiles,
  files,
  views,
  labels,
  title,
  onOpenCard,
  onAskHelper,
}: {
  projectId: string;
  onFiles: (files: FileMap) => void;
  files: FileMap;
  views: FileView[];
  labels: Record<string, CardLabel>;
  title: string;
  onOpenCard: (id: string) => void;
  onAskHelper: (text: string) => void;
}) {
  const map = useMemo(() => buildProjectMap(files, views), [files, views]);
  const [testing, setTesting] = useState(false);
  const { plan } = map;
  const passing = plan.tests.filter((t) => t.done).length;
  const nextTest = plan.tests.find((t) => !t.done);
  // A small live picture of the game for built screens (no input; it just runs).
  const thumb = useMemo(() => buildPreview(files).html, [files]);

  return (
    <div className="pmap">
      <DreamPanel dream={plan.dream} onAskHelper={onAskHelper} />
      {testing && (
        <TestLab
          projectId={projectId}
          files={files}
          onClose={() => setTesting(false)}
          onFiles={onFiles}
          onBugReport={(text) => {
            setTesting(false);
            onAskHelper(text);
          }}
        />
      )}
      <section className="panel plan-panel" aria-label="The plan">
        <div>
          <span className="eyebrow">The plan · from PROJECT.md</span>
          <h2>{plan.title ?? title}</h2>
        </div>
        {plan.about && <p className="ink2">{plan.about}</p>}
        {plan.forWhom ? (
          <p className="ink2">
            <b>For:</b> {plan.forWhom}
          </p>
        ) : (
          <p className="hint">Add a line like “For: my little brother, who likes quick games” to PROJECT.md.</p>
        )}
        <div className="col">
          <b>MVP · must have</b>
          {plan.mvp.length === 0 && <p className="hint">List the must-haves as a checklist under ## Plan, like “- [ ] Player moves left and right”.</p>}
          {plan.mvp.map((m, i) => (
            <label key={i} className="check">
              <input type="checkbox" checked={m.done} readOnly />
              {m.text}
            </label>
          ))}
        </div>
        {plan.later.length > 0 && (
          <div className="col">
            <b>Later</b>
            <span className="ink2">{plan.later.join(" · ")}</span>
          </div>
        )}
        <div className="tests">
          <div className="row between">
            <b>Tests</b>
            <span className="muted small">{plan.tests.length ? `${passing} of ${plan.tests.length} passing` : "none yet"}</span>
          </div>
          <div className="bar" aria-hidden="true">
            <div style={{ width: plan.tests.length ? `${(100 * passing) / plan.tests.length}%` : 0 }} />
          </div>
          {plan.tests.length > 0 && (
            <button type="button" className="primary small check-tests" onClick={() => setTesting(true)}>
              Check my tests
            </button>
          )}
          {nextTest ? <span className="ink2 small">Next to try: “{nextTest.text}”</span> : !plan.tests.length && <span className="hint">Write tests under ## Tests, like “- [ ] The score goes up when I hit the mole”.</span>}
        </div>
      </section>

      <div className="pmap-mid">
        <section className="panel" aria-label="Screens">
          <div className="row between">
            <span className="eyebrow">Screens · how the player moves through the game</span>
            <button type="button" className="linklike small" onClick={() => onAskHelper("Plan a new screen for my game: ")}>
              + Plan a new screen
            </button>
          </div>
          <div className="screens">
            {map.screens.map((s, i) => (
              <div key={s.name} className="screen-wrap">
                <div className="screen">
                  {s.built ? (
                    <div className="shot built">
                      <iframe title={`${s.name} screen`} sandbox={PREVIEW_SANDBOX} srcDoc={thumb} tabIndex={-1} aria-hidden="true" />
                    </div>
                  ) : (
                    <div className="shot todo">not built yet</div>
                  )}
                  <b>{s.name}</b>
                  <span className="muted small">
                    {s.built ? "Built" : "Planned"}
                    {s.shows ? ` · ${s.shows}` : ""}
                  </span>
                </div>
                {s.next && i < map.screens.length - 1 && (
                  <div className="screen-arrow">
                    <Arrow size={20} />
                    <span>{s.next.when || s.next.to}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          {plan.screens.length === 0 && <p className="hint">Plan your screens under ## Screens in PROJECT.md, like “- [ ] Game over: score, press R to restart”.</p>}
        </section>

        <section className="panel" aria-label="Rules">
          <span className="eyebrow">Rules · each one is a card in Build</span>
          <div className="rules">
            {map.rules.length === 0 && <p className="hint">No rules yet. When your game reacts to clicks, keys or time, each rule shows up here.</p>}
            {map.rules.map((r, i) =>
              r.built && r.cardId ? (
                <button key={i} type="button" className="rule" onClick={() => onOpenCard(r.cardId!)}>
                  <span className={`rtitle k-${views.flatMap((v) => v.cards).find((c) => c.id === r.cardId)?.kind ?? "event"}`}>{labels[r.cardId]?.title || r.title}</span>
                  <span>{labels[r.cardId]?.steps[0] || r.does}</span>
                </button>
              ) : (
                <button key={i} type="button" className="rule todo" onClick={() => onAskHelper(`${r.title}, ${r.does}.`)}>
                  <span className="rtitle">{r.title}</span>
                  <span>not built yet · {r.does}</span>
                </button>
              ),
            )}
          </div>
        </section>
      </div>

      <div className="pmap-right">
        <section className="panel" aria-label="Where your data lives">
          <span className="eyebrow">Where your data lives</span>
          <div className="data">
            {map.data.length === 0 && <p className="hint">Nothing yet. Things like a score or a list of enemies show up here.</p>}
            {map.data.some((d) => !d.remembered) && <p className="muted small">Everything not marked remembered is forgotten when the game restarts.</p>}
            {map.data.map((d) => (
              <div key={`${d.file}:${d.name}`} className={`datum${d.remembered ? " kept" : ""}`}>
                <code>{d.name}</code>
                <span>
                  {d.about}
                  {d.changedBy.length ? ` · changed by “${d.changedBy.join("”, “")}”` : ""}
                  {d.remembered && (
                    <>
                      {" · "}
                      <b>remembered</b> between games with saveData
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="panel grow" aria-label="Art and sound">
          <span className="eyebrow">Art and sound</span>
          <div className="art">
            {map.art.map((a, i) => (
              <div key={i} className="art-item">
                <div className="art-tile">{a.how.startsWith("emoji") ? <span className="emoji">{a.name}</span> : a.how === "drawn by code" ? <span className="shape" /> : <span className="fileglyph">{a.name.split(".").pop()}</span>}</div>
                {a.how.startsWith("emoji") ? "Emoji" : a.name}
                <span className="muted small">{a.how}</span>
              </div>
            ))}
            <button type="button" className="art-item add" onClick={() => onAskHelper("I want to add art or sound to my game: ")}>
              <span className="art-tile">+</span>
              Add art or sound
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
